import {
	TransportAxle, TransportPlace, TransportPriceEntry, TransportRoutePrice, TransportVehicleClass,
	TransportVolumeCategory
} from "../../../system/Domain";
import { maximumNumberOfVehicles, vehicleClassLabels, volumeCategoryLabels } from "./transportPriceLabels";

/*
 * Editing model for the prices of one list. Transport prices are stored per route (from + to), but in reality they come
 * in clusters: the same prices apply to a whole range of destination areas (e.g. 01-09). The editor therefore works
 * with bands: per origin and vehicle class / volume category, a band is a set of destinations (text like "01-09, 12")
 * together with one price per quantity. Bands are derived from the routes when loading and expanded to routes again when saving.
 */

export type Price = number | "";

export interface Band {
	id: string;
	/** Text like "01-09, 12, Dorfmark" */
	destinations: string;
	/** Index = quantity - 1. Own axle only uses index 0. */
	prices: Price[];
}

/** "own", "class:Pkw" or "volume:M" */
export type TypeKey = string;

export interface OriginModel {
	origin: TransportPlace;
	bands: Record<TypeKey, Band[]>;
}

export type EditorModel = Record<string, OriginModel>;

export interface CompoundOption {
	id: string;
	name: string;
}

export const ownTypeKey: TypeKey = "own";

export const placeKey = (place: TransportPlace) => `${place.kind}:${place.key ?? place.name}`;

export const placeLabel = (place: TransportPlace) => place.kind === "Area" ? `PLZ ${place.key}` : place.name ?? place.key ?? "";

export const classTypeKey = (vehicleClass: TransportVehicleClass): TypeKey => `class:${vehicleClass}`;
export const volumeTypeKey = (volume: TransportVolumeCategory): TypeKey => `volume:${volume}`;

export const typeKeyLabel = (key: TypeKey) => {
	const [kind, value] = key.split(":");
	return kind === "class" ? vehicleClassLabels[value as TransportVehicleClass] : kind === "volume" ? volumeCategoryLabels[value as TransportVolumeCategory] : "Preis je Fahrzeug";
};

export const allTypeKeys: TypeKey[] = [
	...(Object.keys(vehicleClassLabels) as TransportVehicleClass[]).map(classTypeKey),
	...(Object.keys(volumeCategoryLabels) as TransportVolumeCategory[]).map(volumeTypeKey)
];

const entryTypeKey = (entry: TransportPriceEntry): TypeKey =>
	entry.vehicleClass ? classTypeKey(entry.vehicleClass) : entry.volumeCategory ? volumeTypeKey(entry.volumeCategory) : ownTypeKey;

const emptyPrices = (): Price[] => Array.from({ length: maximumNumberOfVehicles }, () => "");

let nextId = 0;
export const createBandId = () => `band-${++nextId}`;

export const createBand = (): Band => ({ id: createBandId(), destinations: "", prices: emptyPrices() });

const pad = (value: string) => value.padStart(2, "0");

export const formatDestinations = (places: TransportPlace[], areaCodes: string[]) => {
	const areaIndexes = places
		.filter(p => p.kind === "Area")
		.map(p => areaCodes.indexOf(p.key!))
		.filter(i => i >= 0)
		.sort((a, b) => a - b);

	const parts: string[] = [];
	for (let i = 0; i < areaIndexes.length;) {
		let end = i;
		while (end + 1 < areaIndexes.length && areaIndexes[end + 1] === areaIndexes[end] + 1) {
			end++;
		}

		parts.push(end - i >= 2 ? `${areaCodes[areaIndexes[i]]}-${areaCodes[areaIndexes[end]]}` : areaIndexes.slice(i, end + 1).map(x => areaCodes[x]).join(", "));
		i = end + 1;
	}

	const compounds = places.filter(p => p.kind === "Compound").map(p => p.name ?? p.key ?? "").sort((a, b) => a.localeCompare(b));

	return [...parts, ...compounds].join(", ");
};

export const parseDestinations = (text: string, areaCodes: string[], compounds: CompoundOption[]) => {
	const places: TransportPlace[] = [];
	const errors: string[] = [];
	const seen = new Set<string>();

	const add = (place: TransportPlace) => {
		const key = placeKey(place);
		if (!seen.has(key)) {
			seen.add(key);
			places.push(place);
		}
	};

	for (const token of text.split(/[,;]+/).map(t => t.trim()).filter(t => t)) {
		const range = token.match(/^(\d{1,2})\s*[-–]\s*(\d{1,2})$/);
		if (range) {
			const [from, to] = [pad(range[1]), pad(range[2])];
			const codes = areaCodes.filter(c => c >= from && c <= to);
			if (from > to || codes.length === 0) {
				errors.push(`Ungültiger Bereich '${token}'`);
			}

			codes.forEach(key => add({ kind: "Area", key, name: key }));
			continue;
		}

		if (/^\d{1,2}$/.test(token)) {
			const code = pad(token);
			if (areaCodes.includes(code)) {
				add({ kind: "Area", key: code, name: code });
			} else {
				errors.push(`'${code}' ist kein gültiges PLZ-Gebiet`);
			}
			continue;
		}

		const compound = compounds.find(c => c.name.toLowerCase() === token.toLowerCase());
		if (compound) {
			add({ kind: "Compound", key: compound.id, name: compound.name });
		} else {
			errors.push(`Unbekanntes Ziel '${token}'`);
		}
	}

	return { places, errors };
};

const placeOrder = (areaCodes: string[]) => (place: TransportPlace) =>
	place.kind === "Area" ? areaCodes.indexOf(place.key!) : 1000;

/** Clusters routes to bands: destinations with identical prices (per origin and type) share a band. */
export const buildModel = (routes: Pick<TransportRoutePrice, "from" | "to" | "prices">[], areaCodes: string[]): EditorModel => {
	type Destination = { place: TransportPlace; prices: Price[] };
	const collected: Record<string, { origin: TransportPlace; types: Record<TypeKey, Record<string, Destination>> }> = {};

	for (const route of routes) {
		const originKey = placeKey(route.from);
		const origin = collected[originKey] ?? (collected[originKey] = { origin: route.from, types: {} });

		for (const entry of route.prices) {
			const typeKey = entryTypeKey(entry);
			const type = origin.types[typeKey] ?? (origin.types[typeKey] = {});
			const toKey = placeKey(route.to);
			const destination = type[toKey] ?? (type[toKey] = { place: route.to, prices: emptyPrices() });
			destination.prices[(entry.quantity ?? 1) - 1] = entry.pricePerVehicle;
		}
	}

	const order = placeOrder(areaCodes);
	const model: EditorModel = {};
	for (const [originKey, { origin, types }] of Object.entries(collected)) {
		const bands: Record<TypeKey, Band[]> = {};

		for (const [type, destinations] of Object.entries(types)) {
			const groups = new Map<string, { places: TransportPlace[]; prices: Price[] }>();
			for (const destination of Object.values(destinations)) {
				const signature = JSON.stringify(destination.prices);
				const group = groups.get(signature) ?? { places: [], prices: destination.prices };
				group.places.push(destination.place);
				groups.set(signature, group);
			}

			bands[type] = Array.from(groups.values())
				.sort((a, b) => Math.min(...a.places.map(order)) - Math.min(...b.places.map(order)))
				.map(g => ({ id: createBandId(), destinations: formatDestinations(g.places, areaCodes), prices: g.prices }));
		}

		model[originKey] = { origin, bands };
	}

	return model;
};

/** Expands the bands to routes. Returns all problems found, the routes are only usable if there are none. */
export const buildRoutes = (
	model: EditorModel,
	axle: TransportAxle,
	areaCodes: string[],
	compounds: CompoundOption[]
) => {
	const errors: string[] = [];
	const routes = new Map<string, Pick<TransportRoutePrice, "from" | "to" | "prices">>();

	for (const { origin, bands } of Object.values(model)) {
		for (const [type, typeBands] of Object.entries(bands)) {
			const context = axle === "Own" ? placeLabel(origin) : `${placeLabel(origin)} / ${typeKeyLabel(type)}`;
			const used = new Set<string>();

			for (const band of typeBands) {
				const hasPrice = band.prices.some(p => p !== "");
				if (!band.destinations.trim() && !hasPrice) {
					continue;
				}

				const { places, errors: parseErrors } = parseDestinations(band.destinations, areaCodes, compounds);
				parseErrors.forEach(e => errors.push(`${context}: ${e}`));
				if (!places.length && !parseErrors.length) {
					errors.push(`${context}: Eine Preiszeile hat keine Ziele`);
				}

				if (!hasPrice) {
					errors.push(`${context}: '${band.destinations}' hat keine Preise`);
				}

				if (band.prices.some(p => p !== "" && (p < 0 || !isFinite(p)))) {
					errors.push(`${context}: Preise dürfen nicht negativ sein`);
				}

				for (const place of places) {
					const destinationKey = placeKey(place);
					if (placeKey(place) === placeKey(origin)) {
						errors.push(`${context}: Start und Ziel sind identisch (${placeLabel(place)})`);
						continue;
					}

					if (used.has(destinationKey)) {
						errors.push(`${context}: ${placeLabel(place)} kommt in mehreren Preiszeilen vor`);
						continue;
					}

					used.add(destinationKey);

					const routeKey = `${placeKey(origin)}>${destinationKey}`;
					const route = routes.get(routeKey) ?? { from: origin, to: place, prices: [] };
					routes.set(routeKey, route);

					if (axle === "Own") {
						if (band.prices[0] !== "") {
							route.prices.push({ pricePerVehicle: band.prices[0] as number });
						}
						continue;
					}

					const [kind, value] = type.split(":");
					band.prices.forEach((price, index) => {
						if (price !== "") {
							route.prices.push({
								quantity: index + 1,
								vehicleClass: kind === "class" ? value as TransportVehicleClass : null,
								volumeCategory: kind === "volume" ? value as TransportVolumeCategory : null,
								pricePerVehicle: price
							});
						}
					});
				}
			}
		}
	}

	return { routes: Array.from(routes.values()).filter(r => r.prices.length > 0), errors };
};

export const countBands = (model: EditorModel) =>
	Object.values(model).reduce((sum, o) => sum + Object.values(o.bands).reduce((s, b) => s + b.length, 0), 0);
